import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, KeyRound } from 'lucide-react';
import { authApi } from '../api/client';

/**
 * Landing page for the emailed reset link (/reset-password?token=...).
 *
 * The backend collapses every token failure into one generic 400, so the
 * error copy here stays equally generic — including for a missing token,
 * which just means someone opened the URL without the query string.
 */
export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (newPassword.length < 8) {
            setError('New password must be at least 8 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setIsLoading(true);
        try {
            await authApi.resetPassword(token, newPassword);
            setDone(true);
        } catch (err) {
            setError(
                err.response?.status === 400
                    ? err.response.data?.message || 'This reset link is invalid or has expired.'
                    : 'Something went wrong. Please try again.',
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <h1 className="font-display text-3xl font-semibold text-slate-900">
                        Loanworker
                    </h1>
                    <p className="text-slate-600 mt-1">Choose a new password</p>
                </div>

                {done ? (
                    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 space-y-4">
                        <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                            <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                            <span>Your password has been reset.</span>
                        </div>
                        <Link
                            to="/login"
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-md"
                        >
                            Sign in
                        </Link>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 space-y-4"
                    >
                        {error && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <div>
                            <label
                                htmlFor="new-password"
                                className="block text-sm font-medium text-slate-700 mb-1"
                            >
                                New password
                            </label>
                            <input
                                id="new-password"
                                type="password"
                                required
                                minLength={8}
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                disabled={isLoading}
                            />
                            <p className="text-xs text-slate-500 mt-1">At least 8 characters.</p>
                        </div>

                        <div>
                            <label
                                htmlFor="confirm-password"
                                className="block text-sm font-medium text-slate-700 mb-1"
                            >
                                Confirm new password
                            </label>
                            <input
                                id="confirm-password"
                                type="password"
                                required
                                minLength={8}
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                disabled={isLoading}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-md disabled:bg-slate-300 disabled:cursor-not-allowed"
                        >
                            <KeyRound size={16} />
                            {isLoading ? 'Resetting...' : 'Reset password'}
                        </button>
                    </form>
                )}

                {!done && (
                    <p className="text-center text-sm text-slate-600 mt-6">
                        Link expired?{' '}
                        <Link
                            to="/forgot-password"
                            className="text-brand-600 hover:text-brand-700 font-medium"
                        >
                            Request a new one
                        </Link>
                    </p>
                )}
            </div>
        </div>
    );
}
